import { useQuery } from '@tanstack/react-query'
import { X, RotateCcw } from 'lucide-react'
import { supabase, isDemoMode } from '@/lib/supabase'
import { useFilters } from '@/lib/store'
import { PARCELAS, DISCIPLINES, ACTIVITY_FAMILY_NAMES } from '@/lib/constants'
import type { ActivityStatus, DisciplineType } from '@/types'

interface FilterPanelProps {
  onClose?: () => void
}

export default function FilterPanel({ onClose }: FilterPanelProps) {
  const {
    filters,
    setParcelFilter,
    setDiscipline,
    setFamilyFilter,
    setSubcontractor,
    setStatusFilter,
    setDateRange,
    toggleGlobals,
    resetFilters,
  } = useFilters()

  // Fetch subcontractors for dropdown
  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      if (isDemoMode) return [
        { id: 'sub-01', name: 'Mecano Trackers SL' },
        { id: 'sub-02', name: 'Electra Obras SA' },
        { id: 'sub-03', name: 'Civil Navabuena UTE' },
      ]
      const { data, error } = await supabase
        .from('companies')
        .select('id, name')
        .eq('is_active', true)
        .order('name')
      if (error) throw error
      return data ?? []
    },
    staleTime: 10 * 60 * 1000,
  })

  const activityStatuses: { value: ActivityStatus; label: string }[] = [
    { value: 'NO_INICIADA',          label: 'No iniciada' },
    { value: 'PLANIFICADA',          label: 'Planificada' },
    { value: 'EN_CURSO',             label: 'En curso' },
    { value: 'FINALIZADA',           label: 'Finalizada' },
    { value: 'BLOQUEADA',            label: 'Bloqueada' },
    { value: 'PENDIENTE_DOCUMENTAL', label: 'Pend. documental' },
  ]

  const toggleParcel = (num: number) => {
    const current = filters.parcels
    if (current.includes(num)) {
      setParcelFilter(current.filter((p) => p !== num))
    } else {
      setParcelFilter([...current, num])
    }
  }

  const activeCount = [
    filters.parcels.length > 0,
    filters.discipline !== null,
    filters.activityFamily !== null,
    filters.subcontractor !== null,
    filters.status !== null,
    filters.dateFrom !== null || filters.dateTo !== null,
  ].filter(Boolean).length

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-panel overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Filtros</h3>
          {activeCount > 0 && (
            <span className="text-xs bg-brand-100 text-brand-700 rounded-full px-2 py-0.5 font-medium">
              {activeCount} activos
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {activeCount > 0 && (
            <button
              onClick={resetFilters}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" />
              Resetear
            </button>
          )}
          {onClose && (
            <button onClick={onClose} className="p-1 rounded text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto scrollbar-thin">
        {/* Parcelas multi-select */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
            Parcelas
            {filters.parcels.length > 0 && (
              <button
                className="ml-2 text-xs text-brand-600 font-normal normal-case"
                onClick={() => setParcelFilter([])}
              >
                Limpiar ({filters.parcels.length})
              </button>
            )}
          </label>
          <div className="grid grid-cols-5 gap-1">
            {PARCELAS.map((p) => (
              <button
                key={p.number}
                onClick={() => toggleParcel(p.number)}
                className={`
                  text-xs py-1 px-1.5 rounded font-medium transition-colors border
                  ${filters.parcels.includes(p.number)
                    ? 'bg-brand-600 text-white border-brand-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-brand-300'
                  }
                `}
              >
                {String(p.number).padStart(2, '0')}
              </button>
            ))}
          </div>
        </div>

        {/* Disciplina */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
            Disciplina
          </label>
          <select
            value={filters.discipline ?? ''}
            onChange={(e) => setDiscipline((e.target.value || null) as DisciplineType | null)}
            className="input-base text-xs"
          >
            <option value="">Todas las disciplinas</option>
            {DISCIPLINES.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>

        {/* Familia de actividad */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
            Familia de actividad
          </label>
          <select
            value={filters.activityFamily ?? ''}
            onChange={(e) => setFamilyFilter(e.target.value || null)}
            className="input-base text-xs"
          >
            <option value="">Todas las familias</option>
            {Object.entries(ACTIVITY_FAMILY_NAMES).map(([code, name]) => (
              <option key={code} value={code}>{code}. {name}</option>
            ))}
          </select>
        </div>

        {/* Subcontratista */}
        {companies && companies.length > 0 && (
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
              Subcontratista
            </label>
            <select
              value={filters.subcontractor ?? ''}
              onChange={(e) => setSubcontractor(e.target.value || null)}
              className="input-base text-xs"
            >
              <option value="">Todos</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Estado */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
            Estado
          </label>
          <select
            value={filters.status ?? ''}
            onChange={(e) => setStatusFilter((e.target.value || null) as ActivityStatus | null)}
            className="input-base text-xs"
          >
            <option value="">Todos los estados</option>
            {activityStatuses.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        {/* Date range */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
            Rango de fechas
          </label>
          <div className="flex gap-2">
            <input
              type="date"
              value={filters.dateFrom ?? ''}
              onChange={(e) => setDateRange(e.target.value || null, filters.dateTo)}
              className="input-base text-xs flex-1"
              placeholder="Desde"
            />
            <input
              type="date"
              value={filters.dateTo ?? ''}
              onChange={(e) => setDateRange(filters.dateFrom, e.target.value || null)}
              className="input-base text-xs flex-1"
              placeholder="Hasta"
            />
          </div>
        </div>

        {/* Show globals toggle */}
        <div className="flex items-center justify-between py-1">
          <div>
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Actividades globales
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              Incluir línea 132kV, 400kV, edificios, etc.
            </p>
          </div>
          <button
            role="switch"
            aria-checked={filters.showGlobals}
            onClick={toggleGlobals}
            className={`
              relative inline-flex w-10 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
              ${filters.showGlobals ? 'bg-brand-600' : 'bg-gray-300'}
            `}
          >
            <span
              className={`
                inline-block w-4 h-4 rounded-full bg-white shadow transition-transform mt-0.5
                ${filters.showGlobals ? 'translate-x-5' : 'translate-x-0.5'}
              `}
            />
          </button>
        </div>
      </div>
    </div>
  )
}
